import { api } from '@goaltree/backend/convex/_generated/api'
import { coachDisplayName, coachNameOverride } from '@goaltree/backend/convex/lib/coach'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { useTranslation } from 'react-i18next'

import { optimisticSetCoachName } from '@/lib/prefs-optimistic'

type CoachPrefs =
  | { coachVoice?: string; coachName?: string; coachNames?: Record<string, string> }
  | null
  | undefined

// The coach's name as the user sees it: their own name for that coach, else the
// voice's default ("Your voice" for a cloned voice).
export function useCoachDisplayName(prefs: CoachPrefs, voice = prefs?.coachVoice) {
  const { t } = useTranslation()
  return coachDisplayName(voice, coachNameOverride(prefs, voice), t('coach.customVoice'))
}

export function useCoachIdentity() {
  const { t } = useTranslation()
  const { isAuthenticated } = useConvexAuth()
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip') as
    | CoachPrefs
    | Record<string, unknown>
  const p = prefs as CoachPrefs
  const voice = p?.coachVoice
  const customName = coachNameOverride(p, voice)
  const defaultName = coachDisplayName(voice, undefined, t('coach.customVoice'))
  return {
    loaded: prefs !== undefined,
    voice,
    customName,
    defaultName,
    name: customName ?? defaultName,
  }
}

export function useSetCoachName() {
  return useMutation(api.preferences.setCoachName).withOptimisticUpdate(optimisticSetCoachName)
}
